import {Injectable} from "@angular/core";
import {CookieService} from "ngx-cookie-service";
import {EigentuemerPrintOptionsMeta, GrundstueckPrintOptionsMeta, RechtPrintOptionsMeta} from "../gb-api";
import {HistoryMode} from "./HistoryMode";

@Injectable()
export class AppAuszugCookieService {

  private expiresDays = 365;
  private readonly falseString = "false";
  private readonly trueString = "true";

  constructor(private cookieService: CookieService) {
  }

  /**
   * As long we use JSF-Printing, we need to provide AnzeigeOptionen as Cookie Values.
   * The Default Anzeige Optionen are configured on the Server and read at the beginning {@Link AppPrintConfig}.
   */
  public saveGrundstueckCookies(printConfig: GrundstueckPrintOptionsMeta, noOverwrite?: boolean) {
    const cookieSettings = new Map([]);
    const cookiemapping = this.getGrundstueckCookieMapping();
    cookieSettings.set("VERTRAGSTEXTE", this.trueString);
    cookieSettings.set("GLAEUBIGER_ADRESSE", this.trueString);
    cookieSettings.set("ANZEIGE_AMTLICHER_WERT", this.trueString);
    cookieSettings.set("ANZEIGE_DOMINIERTE_GRUNDSTUECKE", this.trueString);
    cookieSettings.set("GRUNDSTUECK_GEGENEINTRAG_PFAND", this.trueString);
    cookieSettings.set("GRUNDSTUECK_GEGENEINTRAG_LR", this.trueString);
    this.saveCookies(printConfig, cookiemapping, cookieSettings, noOverwrite ? noOverwrite : false);
  }

  private saveCookies(printConfig: any, cookieMapping: Map<string, string>, cookieSettings: Map<{}, {}>, noOverwrite: boolean) {
    cookieMapping.forEach((printOptionName: string, cookieName: string) => {
      cookieSettings.set(cookieName, printConfig.hasOwnProperty(printOptionName) ? printConfig[printOptionName] : this.falseString);
    });
    cookieSettings.forEach((value: string, key: string) => {
      if (!noOverwrite || !this.cookieService.get(key)) {
        this.saveCookie(key, value);
      }
    });
  }

  public saveRechtCookies(printConfig: RechtPrintOptionsMeta, noOverwrite?: boolean) {
    this.saveCookies(printConfig, this.getRechtCookieMapping(), new Map([]), noOverwrite ? noOverwrite : false);
  }

  public saveEigentuemerCookies(printConfig: EigentuemerPrintOptionsMeta, noOverwrite?: boolean) {
    const cookieSettings = new Map([
      ["UEBRIGE_BERECHTIGTE_ADRESSE", this.trueString]
    ]);
    this.saveCookies(printConfig, this.getEigentuemerCookieMapping(), cookieSettings, noOverwrite ? noOverwrite : false);
  }

  public updateHistorisiertCookie(historisierValue: boolean) {
    this.saveCookie("HISTORY", historisierValue.toString());
    this.saveCookie("LOESCHGESCHAEFT", historisierValue.toString());
  }

  public readHistorisiertCookie(): HistoryMode.HistoryMode {
    if (this.cookieService.get("HISTORY") === 'true') {
      return HistoryMode.HistoryMode.MIT_HIST;
    } else {
      return HistoryMode.HistoryMode.OHNE_HIST;
    }
  }

  private saveCookie(key: string, value: string) {
    if (location.protocol === "https:") {
      this.cookieService.set(key, value, this.expiresDays, "/", "", true);
    } else {
      this.cookieService.set(key, value, this.expiresDays, "/");
    }
  }

  private getEigentuemerCookieMapping() {
    return new Map([
      ["EIGENTUEMER_ADRESSE", 'adresseAufbereiten'],
    ]);
  }

  private getRechtCookieMapping() {
    return new Map([
      ["VERTRAGSTEXTE_RECHT", 'vertragstextAufbereiten'],
    ]);
  }

  private getGrundstueckCookieMapping() {
    return new Map([
      ["GRUNDSTUECK_EREID", 'ereidsAufbereiten'],
      ["GRUNDSTUECK_SERVITUT_ID", 'standardrechtIdsAufbereiten'],
      ["GRUNDSTUECK_WEITERE_RECHTSGRUENDE", 'weitereRechtsgruendeAufbereiten'],
      ["GRUNDSTUECK_EIGENTUM", 'eigentumErweitern'],
      ["GRUNDSTUECK_PFANDRECHT_ID", 'pfandrechtIdsAufbereiten'],
      ["ANZEIGE_ANMERKUNGEN", 'anmerkungen'],
      ["ANZEIGE_VORMERKUNGEN", 'vormerkungen'],
      ["ANZEIGE_HAENGIGE_GESCHAEFTE", 'haengigeGeschaefteAufbereiten'],
      ["ANZEIGE_DIENSTBARKEITEN", 'dienstbarkeiten'],
      ["ANZEIGE_GRUNDLASTEN", 'grundlasten'],
      ["ANZEIGE_GRUNDSTUECK_BESCHREIBUNG", 'grundstueckBeschreibung'],
      ["ANZEIGE_GRUNDPFANDRECHTE", 'grundpfandrechte'],
      ["ANZEIGE_EIGENTUM", 'eigentum'],
      ["ANZEIGE_EIGENTUEMER_ADRESSE", 'eigentuemerMitAdresse'],
      ["ANZEIGE_VERTRAGSTEXTE", 'vertragstexteAufbereiten'],
      ["ANZEIGE_OEREBKATASTER", 'oerebKataster']
    ]);
  }

  private readCookies(model: any, cookieMapping: Map<string, string>): any {
    cookieMapping.forEach((printOptionName: string, cookieName: string) => {
      let value = false;
      if (this.cookieService.get(cookieName) === this.trueString) {
        value = true;
      }
      model[printOptionName] = value;
    });
    return model;
  }

  public readGrundstueckCookies(model: GrundstueckPrintOptionsMeta): GrundstueckPrintOptionsMeta {
    return this.readCookies(model, this.getGrundstueckCookieMapping());
  }

  public readEigentuemerCookies(model: GrundstueckPrintOptionsMeta): GrundstueckPrintOptionsMeta {
    return this.readCookies(model, this.getEigentuemerCookieMapping());
  }

  public readRechtCookies(model: GrundstueckPrintOptionsMeta): GrundstueckPrintOptionsMeta {
    return this.readCookies(model, this.getRechtCookieMapping());
  }

}
